import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type {
  AdminPlanOverridePayload,
  AdminReconcileResult,
  MirroredBillingState,
} from '@/types/api';

/**
 * Admin-only billing-repair API. Mirrors
 * `server/src/modules/billing/admin-billing.controller.ts`.
 *
 * Kept separate from `adminMonitoringService` (which is read-only aggregates
 * plus delete) so the mutation surface for billing state is one small,
 * obvious file.
 */
export const adminBillingService = {
  /**
   * Re-read Stripe and re-mirror the account's billing columns. Safe to call
   * repeatedly — it delegates to the same write path the Stripe webhook uses,
   * so a no-op reconcile is genuinely a no-op.
   */
  reconcile(accountId: string) {
    return apiRequest<AdminReconcileResult>(apiRoutes.adminBilling.reconcile(accountId), {
      method: 'POST',
    });
  },

  /**
   * Comp an account onto a paid tier without Stripe, or revoke it to free.
   *
   * The server returns 409 when the account has a live Stripe subscription
   * (the override would be reverted by the next webhook) and 400 when the
   * tier and status disagree. Surface `ApiError.message` either way — it
   * explains which case was hit.
   */
  overridePlan(accountId: string, payload: AdminPlanOverridePayload) {
    return apiRequest<MirroredBillingState>(apiRoutes.adminBilling.plan(accountId), {
      method: 'PATCH',
      body: payload,
    });
  },
};
